/* eslint-disable no-case-declarations */
import React, { useState } from "react";

import "react-big-calendar/lib/css/react-big-calendar.css";
import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Calendar } from "react-big-calendar";
import { useTranslation } from "react-i18next";

import { Timezone } from "../../models/timezone";
import TimezoneService from "../../services/timezone.service";
import { getCalendarCulture } from "../../util/getDateFnsLocale";
import SkeletonComponent from "../Generic/Skeleton";
import useStyles from "./styles";
import { CalendarProps, DiaryType, localizer } from "./types";

export const DiaryCalendar: React.FC<CalendarProps> = (props) => {
  const {
    viewMode = DiaryType.MONTH,
    events = [],
    onSelectEvent,
    onSelectDate,
    onRangeChange,
    showAllEvents = false,
    onShowMoreEvents,
    loadingEvents = false,
    date,
    badgeComponent: Badge,
    disablePadding,
  } = props;

  const classes = useStyles({ disablePadding });
  const { t: translate, i18n } = useTranslation();
  const culture = getCalendarCulture(i18n.language);

  const actualDate = date || new Date();

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [timezone, setTimezone] = useState<Timezone | null>(null);

  const { isFetching: fetchingTimezone } = useQuery(
    ["timezones.list"],
    () =>
      TimezoneService.getAll({
        name: browserTimezone,
      }),
    {
      enabled: !!browserTimezone,
      onSuccess: (response) => {
        if (
          response &&
          response.data &&
          response.data instanceof Array &&
          response.data.length > 0
        ) {
          setTimezone(response.data[0]);
        }
      },
    }
  );

  const getTimezoneOffset = () => {
    if (timezone && timezone.name && timezone.name === browserTimezone) {
      return <span className={classes.gmtLabel}>{`GMT ${timezone.gmtOffset}`}</span>;
    }

    return <span></span>;
  };

  const renderTimezoneOffset = () => {
    return (
      <div className={classes.gmtContainer}>
        {fetchingTimezone ? <CircularProgress size={16} color="primary" /> : getTimezoneOffset()}
      </div>
    );
  };

  const getContainerClassesbyViewMode = () => {
    return classNames(classes.calendar, {
      [classes.month]: viewMode === DiaryType.MONTH,
      [classes.week]: viewMode === DiaryType.WEEK,
      [classes.day]: viewMode === DiaryType.DAY,
    });
  };

  return (
    <div className={getContainerClassesbyViewMode()}>
      <SkeletonComponent isLoading={loadingEvents} classesName={classes.skeleton}>
        <Calendar
          showMultiDayTimes={viewMode === DiaryType.WEEK}
          defaultView={viewMode}
          localizer={localizer}
          culture={culture}
          toolbar={false}
          events={events}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectDate}
          selectable={false}
          onRangeChange={onRangeChange}
          onShowMore={onShowMoreEvents}
          showAllEvents={showAllEvents}
          messages={{
            showMore: (count) => `${translate("see")} ${count} ${translate("moreTasks")}`,
          }}
          timeslots={viewMode === DiaryType.WEEK ? 2 : undefined}
          date={actualDate}
          onNavigate={() => {}}
          views={[viewMode]}
          components={{
            eventWrapper: Badge
              ? (eventWrapProps: any) => <Badge {...eventWrapProps} {...props} />
              : undefined,
            timeGutterHeader: () => renderTimezoneOffset(),
            month: {
              dateHeader: (params) => <div className={classes.dateLabel}>{params.label}</div>,
            },
          }}
        />
      </SkeletonComponent>
    </div>
  );
};

export * from "./types";
